<template>
  <div class="log-box">
    <div class="lb-toolbar">
      <div class="lb-toolbar-left">
        <span class="lb-title">{{ title }}</span>
        <button
          type="button"
          class="lb-chip"
          :class="{ active: filter === 'all' }"
          @click="filter = 'all'">
          전체 <b>{{ counts.total }}</b>
        </button>
        <button
          type="button"
          class="lb-chip lb-chip--success"
          :class="{ active: filter === 'success' }"
          @click="filter = 'success'">
          성공 <b>{{ counts.success }}</b>
        </button>
        <button
          type="button"
          class="lb-chip lb-chip--fail"
          :class="{ active: filter === 'fail' }"
          @click="filter = 'fail'">
          실패 <b>{{ counts.fail }}</b>
        </button>
      </div>

      <div class="lb-toolbar-right">
        <div class="lb-search">
          <input
            v-model="keyword"
            type="text"
            placeholder="로그 검색"
            spellcheck="false" />
          <button
            v-if="keyword"
            type="button"
            class="lb-search-clear"
            title="검색어 지우기"
            @click="keyword = ''">×</button>
        </div>
        <button
          type="button"
          class="lb-btn"
          :class="{ on: expandAll }"
          :title="expandAll ? '한 줄로 접기' : '전체 펼치기'"
          @click="expandAll = !expandAll">
          {{ expandAll ? '접기' : '펼치기' }}
        </button>
        <button
          type="button"
          class="lb-btn"
          :class="{ on: autoFollow }"
          title="새 로그가 오면 자동으로 따라갑니다"
          @click="toggleFollow">
          자동추적
        </button>
        <button
          type="button"
          class="lb-btn"
          title="보이는 로그를 클립보드에 복사"
          :disabled="!filteredLogs.length"
          @click="copyLogs">
          {{ copied ? '복사됨' : '복사' }}
        </button>
        <button
          v-if="clearable"
          type="button"
          class="lb-btn"
          title="로그 지우기"
          :disabled="!logs.length"
          @click="$emit('clear')">
          지우기
        </button>
      </div>
    </div>

    <div class="lb-body" ref="logBody" :style="{ height: bodyHeight }" @scroll="onScroll">
      <p v-if="!logs.length" class="lb-empty">아직 기록된 로그가 없습니다.</p>
      <p v-else-if="!filteredLogs.length" class="lb-empty">조건에 맞는 로그가 없습니다.</p>

      <div
        v-for="(log, index) in filteredLogs"
        :key="rowKey(log, index)"
        class="log-line lb-row"
        :class="[
          log.isSuccess ? 'success' : 'fail',
          { expanded: expandAll || openRows[rowKey(log, index)] }
        ]"
        :title="buildMessage(log)"
        @click="toggleRow(rowKey(log, index))">
        <span class="lb-dot"></span>
        <span class="lb-time">{{ formatTimestamp(log.timestamp) }}</span>
        <span v-if="log.type" class="lb-tag lb-tag--type">{{ log.type }}</span>
        <span v-if="log.site" class="lb-tag lb-tag--site">{{ log.site }}</span>
        <span class="lb-text">
          <span v-if="metaOf(log)" class="lb-meta">{{ metaOf(log) }}</span>
          <span class="lb-msg">{{ bodyOf(log) }}</span>
        </span>
        <span class="lb-state">{{ log.isSuccess ? '성공' : '실패' }}</span>
      </div>
    </div>

    <button
      v-if="pendingCount"
      type="button"
      class="lb-jump"
      :class="{ bottom: !newestFirst }"
      @click="jumpToLatest">
      새 로그 {{ pendingCount }}건 {{ newestFirst ? '↑' : '↓' }}
    </button>
  </div>
</template>

<script>
import { nextTick } from 'vue';

export default {
  name: 'LogBox',
  emits: ['clear'],
  props: {
    logs: {
      type: Array,
      default: () => []
    },
    // 툴바에 표시할 제목
    title: {
      type: String,
      default: '작업 로그'
    },
    // 본문 높이(px)
    height: {
      type: [Number, String],
      default: 200
    },
    // 최신 로그가 목록 맨 위에 오는지 여부(현재 모든 화면이 내림차순 정렬)
    newestFirst: {
      type: Boolean,
      default: true
    },
    // '지우기' 버튼 노출 여부(부모에서 @clear 처리 필요)
    clearable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      filter: 'all',
      keyword: '',
      expandAll: false,
      openRows: {},
      autoFollow: true,
      atEdge: true,
      pendingCount: 0,
      copied: false
    };
  },
  computed: {
    bodyHeight() {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    },
    counts() {
      const total = this.logs.length;
      const success = this.logs.filter(log => log.isSuccess).length;
      return { total, success, fail: total - success };
    },
    filteredLogs() {
      const keyword = this.keyword.trim().toLowerCase();
      return this.logs.filter(log => {
        if (this.filter === 'success' && !log.isSuccess) return false;
        if (this.filter === 'fail' && log.isSuccess) return false;
        if (keyword && !this.buildMessage(log).toLowerCase().includes(keyword)) return false;
        return true;
      });
    }
  },
  mounted() {
    this.scrollToLatest();
  },
  watch: {
    logs(newLogs, oldLogs) {
      const added = Math.max(0, (newLogs || []).length - (oldLogs || []).length);
      if (this.autoFollow && this.atEdge) {
        this.pendingCount = 0;
        this.scrollToLatest();
      } else if (added) {
        this.pendingCount += added;
      }
    },
    filter() {
      this.scrollToLatest();
    }
  },
  methods: {
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      const pad = value => value.toString().padStart(2, '0');
      return `${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
        `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
    },
    getFallbackMessage(log) {
      const status = log.isSuccess ? '성공' : '실패';
      return `[${log.type}] ${status}`;
    },
    // 태그로 따로 보여주지 않는 부가 정보(수집설정명 · 상품코드 · 상품명)
    metaOf(log) {
      return [log.groupName, log.code, log.name].filter(part => part).join(' · ');
    },
    // 본문에 표시할 실제 메시지
    bodyOf(log) {
      const body = [log.failReason, log.message].filter(part => part).join(' ');
      return body || this.getFallbackMessage(log);
    },
    // 검색·복사·툴팁에 쓰는 한 줄 전체 문자열
    buildMessage(log) {
      const parts = [
        this.formatTimestamp(log.timestamp) + ' :',
        log.type,
        log.site,
        this.metaOf(log),
        this.bodyOf(log)
      ];
      return parts.filter(part => part).join(' ');
    },
    rowKey(log, index) {
      return `${log.regDate || log.timestamp || ''}_${log.code || ''}_${index}`;
    },
    toggleRow(key) {
      this.openRows = { ...this.openRows, [key]: !this.openRows[key] };
    },
    toggleFollow() {
      this.autoFollow = !this.autoFollow;
      if (this.autoFollow) this.jumpToLatest();
    },
    onScroll() {
      const box = this.$refs.logBody;
      if (!box) return;
      this.atEdge = this.newestFirst
        ? box.scrollTop <= 8
        : box.scrollHeight - box.scrollTop - box.clientHeight <= 8;
      if (this.atEdge) this.pendingCount = 0;
    },
    jumpToLatest() {
      this.pendingCount = 0;
      this.atEdge = true;
      this.scrollToLatest();
    },
    scrollToLatest() {
      nextTick(() => {
        const box = this.$refs.logBody;
        if (!box) return;
        box.scrollTop = this.newestFirst ? 0 : box.scrollHeight;
      });
    },
    async copyLogs() {
      const text = this.filteredLogs.map(log => this.buildMessage(log)).join('\n');
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        // 클립보드 권한이 없는 환경(비 HTTPS 등) 대비
        const area = document.createElement('textarea');
        area.value = text;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 1200);
    }
  }
};
</script>

<style lang="scss" scoped>
$lb-border: #e3e6ed;
$lb-muted: #8a92a6;
$lb-text: #2f3542;
$lb-success: #14a06a;
$lb-fail: #e0393e;

.log-box {
  position: relative;
  width: 100%;
  margin-top: 10px;
  background-color: #fff;
  border: 1px solid $lb-border;
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;
  color: $lb-text;
}

/* ── 툴바 ─────────────────────────────── */
.lb-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 10px;
  background-color: #f7f8fb;
  border-bottom: 1px solid $lb-border;
}

.lb-toolbar-left,
.lb-toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.lb-title {
  margin-right: 4px;
  font-size: 13px;
  font-weight: 600;
  color: $lb-text;
}

.lb-chip {
  height: 24px;
  padding: 0 9px;
  border: 1px solid $lb-border;
  border-radius: 12px;
  background-color: #fff;
  color: $lb-muted;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s;

  b {
    margin-left: 3px;
    font-weight: 700;
    color: $lb-text;
  }

  &:hover { border-color: #c8cede; }

  &.active {
    border-color: #4b5563;
    background-color: #4b5563;
    color: #fff;
    b { color: #fff; }
  }

  &--success.active {
    border-color: $lb-success;
    background-color: $lb-success;
  }

  &--fail.active {
    border-color: $lb-fail;
    background-color: $lb-fail;
  }
}

.lb-search {
  position: relative;

  input {
    width: 150px;
    height: 24px;
    padding: 0 22px 0 8px;
    border: 1px solid $lb-border;
    border-radius: 4px;
    background-color: #fff;
    font-size: 12px;
    color: $lb-text;
    outline: none;

    &::placeholder { color: #b3b9c7; }
    &:focus { border-color: #9aa3b8; }
  }
}

.lb-search-clear {
  position: absolute;
  top: 50%;
  right: 3px;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  color: $lb-muted;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover { background-color: #eceff5; }
}

.lb-btn {
  height: 24px;
  padding: 0 9px;
  border: 1px solid $lb-border;
  border-radius: 4px;
  background-color: #fff;
  color: #5b6377;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) { border-color: #c8cede; background-color: #f3f5f9; }

  &.on {
    border-color: #4b5563;
    background-color: #4b5563;
    color: #fff;
  }

  &:disabled { opacity: 0.45; cursor: default; }
}

/* ── 본문 ─────────────────────────────── */
.lb-body {
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0;
  background-color: #fff;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb {
    background-color: #d5d9e3;
    border: 2px solid #fff;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track { background-color: transparent; }
}

.lb-empty {
  padding: 24px 0;
  text-align: center;
  color: #b3b9c7;
  font-size: 13px;
}

.lb-row {
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding: 4px 10px 4px 8px;
  border-left: 3px solid transparent;
  line-height: 1.5;
  cursor: pointer;

  &:hover { background-color: #f7f8fb; }

  &.fail {
    border-left-color: $lb-fail;
    background-color: #fef6f6;

    &:hover { background-color: #fdeeee; }
  }
}

.lb-dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: $lb-success;

  .lb-row.fail & { background-color: $lb-fail; }
}

.lb-time {
  flex: 0 0 auto;
  color: $lb-muted;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.lb-tag {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1.5;
  white-space: nowrap;

  &--type {
    background-color: #eef1f8;
    color: #5b6377;
  }

  &--site {
    background-color: #eaf1fd;
    color: #2f6fd0;
  }
}

.lb-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .lb-row.expanded & {
    overflow: visible;
    white-space: pre-wrap;
    word-break: break-all;
  }
}

.lb-meta {
  margin-right: 6px;
  color: $lb-muted;
}

.lb-msg {
  color: $lb-text;

  .lb-row.fail & { color: #b7282d; }
}

.lb-state {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: $lb-success;

  .lb-row.fail & { color: $lb-fail; }
}

/* ── 새 로그 알림 ─────────────────────── */
.lb-jump {
  position: absolute;
  left: 50%;
  top: 46px;
  transform: translateX(-50%);
  height: 24px;
  padding: 0 12px;
  border: 0;
  border-radius: 12px;
  background-color: #4b5563;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;

  /* 오래된 로그가 위에 쌓이는 경우 알림을 아래쪽에 표시 */
  &.bottom { top: auto; bottom: 10px; }
  &:hover { background-color: #3b4151; }
}
</style>
